import { useEffect, useState } from 'react';
import {
  MEMBER_WALLET_PATH,
  isMemberWalletPath,
  memberSettingsPermissionPath,
  hasExactAssociationPageAccess,
} from './navItems';
import { canAccessMemberPortalEditor, MEMBER_PORTAL_EDITOR_PATH } from './memberPortalAccess';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/auth.store.ts';
import { useEditionStore } from '../../store/edition.store.ts';
import { apiClient } from '../../lib/api.ts';

/**
 * 受保護路由 Wrapper
 * - 未登入時重定向至 /login
 * - 頁面重新整理後 accessToken 遺失，永遠透過 HttpOnly Cookie 呼叫 /auth/refresh 還原
 *   （不依賴 Zustand 的 isAuthenticated 初始狀態，解決 E2E storageState 計時問題）
 * - 檢查 visiblePages：若當前路由不在允許清單，重定向至 /
 */
function ProtectedRoute() {
  const { t } = useTranslation('auth');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const updateAccessToken = useAuthStore((s) => s.updateAccessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const visiblePages = useAuthStore((s) => s.visiblePages);
  const allowedActions = useAuthStore((s) => s.allowedActions);
  const { pathname } = useLocation();

  // 若 accessToken 不在記憶體，永遠先嘗試 /refresh（依賴 HttpOnly Cookie）
  const [status, setStatus] = useState<'checking' | 'ok' | 'failed'>(
    accessToken ? 'ok' : 'checking',
  );

  useEffect(() => {
    // 確保 edition store 已載入（頁面重新整理時需要重新取得）
    if (!useEditionStore.getState().isLoaded) {
      useEditionStore
        .getState()
        .fetchEditionInfo()
        .catch(() => {});
    }

    // accessToken 已在記憶體（登入後的初次渲染），不需再 refresh
    if (accessToken) return;

    apiClient
      .post<{ success: boolean; data: { accessToken: string } }>('/auth/refresh')
      .then((r) => {
        updateAccessToken(r.data.data.accessToken);
        setStatus('ok');
      })
      .catch(() => {
        clearAuth();
        setStatus('failed');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center bg-base-100">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-base-content/50">{t('verifyingLogin')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !accessToken || status === 'failed') {
    return <Navigate to="/login" replace />;
  }

  const normalizedPath = pathname.replace(/\/+$/, '');
  if (!hasExactAssociationPageAccess(pathname, visiblePages)) {
    return <Navigate to={visiblePages?.find((page) => page !== normalizedPath) ?? '/'} replace />;
  }
  // This new destination shares the exact existing settings grant, including on direct entry.
  if (
    (normalizedPath === MEMBER_WALLET_PATH ||
      normalizedPath.startsWith(`${MEMBER_WALLET_PATH}/`)) &&
    (!isMemberWalletPath(normalizedPath) ||
      !visiblePages?.includes(memberSettingsPermissionPath(normalizedPath)))
  ) {
    const fallback = visiblePages?.find((page) => page !== MEMBER_WALLET_PATH) ?? '/';
    return <Navigate to={fallback} replace />;
  }

  // Page-level permission check: redirect to first allowed page if current route is not permitted
  if (visiblePages && visiblePages.length > 0) {
    const isAllowed =
      visiblePages.some((page) => pathname === page || pathname.startsWith(page + '/')) ||
      (pathname === MEMBER_PORTAL_EDITOR_PATH &&
        canAccessMemberPortalEditor(visiblePages, allowedActions));
    if (!isAllowed) {
      // Redirect to first visible page instead of "/" to avoid redirect loop when dashboard is not permitted
      const fallback = visiblePages[0] ?? '/';
      return <Navigate to={fallback} replace />;
    }
  }

  return <Outlet />;
}

export default ProtectedRoute;
